import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Grid,
  Autocomplete,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  CardMedia,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as FileIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import MacroTextField from '../MacroTextField';
import HistologySection from '../HistologySection';
import SpecimenDialog from './SpecimenDialog';
import BarcodeGenerator from './BarcodeGenerator';
import CodeSearchDropdown from '../CodeSearchDropdown';
import apiService from '../../services/api';
import { AccessionService } from '../../services/accession-service';
import AmendmentHistory from './AmendmentHistory';

interface Order {
  id: string;
  accessionId: string;
  patientId: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  accessionLocationId: string;
  clinicalHistory: string;
  accessionedBy: string;
  accessionedById: string;
  grossingTechId: string;
  assignedPathologistId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  amendedFromId?: number;
  amendedAt?: string;
  amendedBy?: string;
  version?: string;
  addendaId?: string;
  orderedBy?: number;
  orderDate?: string;
  createdBy?: string;
  sectionValues?: string;
}

interface Patient {
  id: number;
  patientId: string;
  name: string;
  dob: string;
}

interface User {
  id: number;
  name: string;
  role: string;
}

interface Location {
  location_id: number;
  labName: string;
  location_type: string;
}

interface SourceType {
  id?: number;
  part_code: string;
  part_type: string;
  specimen_part_id?: number;
  collection_method: string;
  collection_datetime: string;
  collectionDatetime?: string;
  site: string;
  condition: string;
  submitted_by: string;
  cpt_code: string;
}

interface UserWithRoles {
  id: number;
  name: string;
  email: string;
  roles?: { id: number; name: string; description?: string }[];
}

interface Attachment {
  file: File;
  includeInReport: boolean;
  previewUrl?: string;
  // Backend fields for existing attachments
  id?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  storagePath?: string;
  uploadedBy?: string;
  createdAt?: string;
  signedUrl?: string;
  // Immediate upload tracking
  uploaded?: boolean;
  downloadUrl?: string;
  filePath?: string;
}

interface OrderDialogProps {
  open: boolean;
  onClose: () => void;
  editingOrder: Order | null;
  isAddendumMode: boolean;
  isAmendMode: boolean;
  onSave: (orderData: any) => Promise<{ success: boolean; orderId: string | null }>;
  onFinalize?: (orderId: string) => Promise<boolean>;
  onOrderSaved?: () => void;
}

const orderStatusOptions = [
  "In Progress",
  "Gross Only",
  "Pending Microscopic",
  "Pending Ancillary",
  "Pending Final Diagnosis",
  "Pending QA Review",
  "Addendum",
  "Held",
  "Completed / Finalized",
  "Amended",
  "Cancelled",
  "Archived"
];

// Helper to get current datetime in user's timezone in 'YYYY-MM-DDTHH:mm' format
function getLocalDateTimeString() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

// Helper to convert ISO datetime string to datetime-local format
function convertToDateTimeLocal(isoString: string | null | undefined): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

// Helper to convert datetime-local format back to ISO format
function convertFromDateTimeLocal(dateTimeLocal: string | null | undefined): string {
  if (!dateTimeLocal || dateTimeLocal.trim() === '') return new Date().toISOString();
  try {
    // Ensure the datetime string has seconds if missing
    let normalizedDateTime = dateTimeLocal.trim();
    if (normalizedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      // Add seconds if missing (format: YYYY-MM-DDTHH:mm)
      normalizedDateTime += ':00';
    }
    
    // Add milliseconds if missing
    if (!normalizedDateTime.includes('.') && normalizedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)) {
      normalizedDateTime += '.000';
    }
    
    // Create a Date object from the datetime-local string (which is in local timezone)
    const date = new Date(normalizedDateTime);
    if (isNaN(date.getTime())) return new Date().toISOString();
    // Return as ISO string (with timezone info)
    return date.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

// Helper to validate file type (images and PDFs only)
function isValidFileType(file: File): boolean {
  const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
  const validPdfTypes = ['application/pdf'];
  return [...validImageTypes, ...validPdfTypes].includes(file.type);
}

// Helper to check if file is an image
function isImageFile(file: File): boolean {
  const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
  return validImageTypes.includes(file.type);
}

// Helper to create preview URL for images
function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

// Helper to get file icon based on type
function getFileIcon(file: File) {
  if (isImageFile(file)) {
    return <ImageIcon />;
  } else if (file.type === 'application/pdf') {
    return <PdfIcon />;
  } else {
    return <FileIcon />;
  }
}

const OrderDialog: React.FC<OrderDialogProps> = ({
  open,
  onClose,
  editingOrder,
  isAddendumMode,
  isAmendMode,
  onSave,
  onFinalize,
  onOrderSaved,
}) => {
     const [form, setForm] = useState<Partial<Order>>({ 
     collectionDatetime: getLocalDateTimeString(),
     status: 'In Progress'
   });
  const [patients, setPatients] = useState<Patient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [specimenClasses, setSpecimenClasses] = useState<any[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [specimenClassesLoading, setSpecimenClassesLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [templateSections, setTemplateSections] = useState<any[]>([]);
  const [sectionFieldValues, setSectionFieldValues] = useState<Record<string, any>>({});
  const [sources, setSources] = useState<SourceType[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [procedureList, setProcedureList] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [orderVersions, setOrderVersions] = useState<Order[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Order | null>(null);
  const [loadedVersionsCache, setLoadedVersionsCache] = useState<Map<string, Order>>(new Map());
  const [versionLoading, setVersionLoading] = useState(false);
  const [addenda, setAddenda] = useState<any[]>([]);
  const [addendumFields, setAddendumFields] = useState({
    reason: '',
    part_affected: '',
    comment: ''
  });
  const [currentUser, setCurrentUser] = useState<UserWithRoles | null>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  
  // Patient creation dialog state
  const [openPatientDialog, setOpenPatientDialog] = useState(false);
  const [patientFormData, setPatientFormData] = useState({
    patientId: '',
    name: '',
    dob: '',
    sex: '',
    mrn: '',
    locationId: '',
  });
  const [patientLocations, setPatientLocations] = useState<any[]>([]);

  const sexOptions = ['Male', 'Female', 'Other'];

  // Specimen parts state
  const [specimenDialogOpen, setSpecimenDialogOpen] = useState(false);
  const [editingSpecimenIdx, setEditingSpecimenIdx] = useState<number | null>(null);
  const [autoCreatedOrderId, setAutoCreatedOrderId] = useState<string | null>(null); // Track auto-created order
  const [specimenForm, setSpecimenForm] = useState<SourceType>({
    part_code: '',
    part_type: '',
    collection_method: '',
    collection_datetime: getLocalDateTimeString(),
    site: '',
    condition: '',
    submitted_by: '',
    cpt_code: '',
  });

  // Confirmation dialog state for sign out
  const [showSignOutConfirmation, setShowSignOutConfirmation] = useState(false);
  const [pendingSignOutAction, setPendingSignOutAction] = useState<(() => Promise<void>) | null>(null);

  // Barcode generator state
  const [barcodeGeneratorOpen, setBarcodeGeneratorOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});

  // Check if SIGNOUT section exists
  const hasSignOutSection = templateSections.some(
    section => section.name && section.name.toLowerCase().trim() === 'signout'
  );

  // Find if the template has a Source section (singular)
  const hasSourceSection = templateSections.some(
    s => s.name && s.name.toLowerCase() === 'source'
  );

  // Dynamic tab value assignment for template sections
  const specialTabs = [
    { label: 'Basic', value: 0 },
    ...(hasSourceSection ? [{ label: 'Parts', value: 1 }] : []),
    { label: 'Files', value: 99 },
    ...(isAddendumMode ? [{ label: 'Addendum', value: 100 }] : []),
  ];

  // Map section id to tab value
  const sectionTabMap: Record<number, number> = {};
  let nextTabValue = hasSourceSection ? 2 : 1;
  
  for (const section of templateSections) {
    if (hasSourceSection && section.name && section.name.toLowerCase() === 'source') continue;
    // Avoid collision with special tab values
    const specialTabValues = specialTabs.map(tab => tab.value);
    while (specialTabValues.includes(nextTabValue)) nextTabValue++;
    sectionTabMap[section.id] = nextTabValue++;
  }

  // Find the SIGNOUT section's tab value
  const signOutTabValue = (() => {
    if (!hasSignOutSection) return null;
    const signOutSection = templateSections.find(
      section => section.name && section.name.toLowerCase().trim() === 'signout'
    );
    return signOutSection ? sectionTabMap[signOutSection.id] : null;
  })();

  // Check if user is currently on the SIGNOUT tab
  const isOnSignOutTab = signOutTabValue !== null && selectedTab === signOutTabValue;

  // Additional debug check - log the values to help identify the issue
  if (process.env.NODE_ENV === 'development') {
    console.log('SIGNOUT Tab Debug:', {
      hasSignOutSection,
      signOutTabValue,
      selectedTab,
      isOnSignOutTab,
      templateSections: templateSections.map(s => ({ id: s.id, name: s.name })),
      sectionTabMap
    });
  }

  // Patient dialog handlers
  const resetPatientForm = () => {
    setPatientFormData({
      patientId: '',
      name: '',
      dob: '',
      sex: '',
      mrn: '',
      locationId: '',
    });
  };

  const handleOpenPatientDialog = async () => {
    resetPatientForm();
    
    // Fetch locations for patient creation - use getAllLocations to get flat structure
    try {
      const response = await apiService.getAllLocations();
      // Filter out HQ locations for patient creation
      const labSiteLocations = response.filter((location: any) => location.location_type !== 'HQ');
      setPatientLocations(labSiteLocations);
    } catch (err) {
      console.error('Error fetching locations for patient creation:', err);
      setPatientLocations([]);
    }
    
    setOpenPatientDialog(true);
  };

  const handleClosePatientDialog = () => {
    setOpenPatientDialog(false);
    resetPatientForm();
  };

  const handlePatientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Create new patient
      const newPatient = await apiService.createPatient(patientFormData);
      
      // Refresh patients list
      const patientsData = await apiService.getPatients(1, 1000);
      setPatients(patientsData.patients || []);
      
      // Automatically select the new patient in the order form
      setForm(prev => ({ ...prev, patientId: String(newPatient.id) }));
      
      // Close the patient dialog
      handleClosePatientDialog();
      
      console.log('Patient created successfully and selected in order.');
    } catch (err) {
      console.error('Error creating patient:', err);
      // You might want to show an error message to the user here
    }
  };

  // Compute patient/specimen class label for dialog title
  const editOrderLabel = (() => {
    if (!editingOrder) return '';
    const patientId = editingOrder.patientId || form.patientId;
    const patient = patientId ? patients.find(p => String(p.id) === String(patientId)) : undefined;
    const patientName = patient ? patient.name : '';
    const specimenClass = editingOrder.specimenClass || form.specimenClass || '';
    let label = '';
    if (patientName) label += ` • ${patientName}`;
    if (specimenClass) label += ` • ${specimenClass}`;
    return label;
  })();



  const isFinalized: boolean = selectedVersion
    ? selectedVersion.id !== orderVersions[0]?.id || (selectedVersion && selectedVersion.status === 'Completed / Finalized')
    : !!(editingOrder && editingOrder.status === 'Completed / Finalized');





  // Filter out HQ locations for the Accession Location dropdown
  const labSiteLocations = locations.filter(location => location.location_type !== 'HQ');

  const fetchOrderSpecimenParts = useCallback(async (orderId: string) => {
    try {
      if (!orderId || orderId.trim() === '') {
        console.warn('Order ID is empty or null, skipping specimen parts fetch');
        return [];
      }
      const data = await apiService.getOrderSpecimenParts(orderId.toString());
      console.log('Raw API response:', data);
      
      // Convert backend response to frontend format
      const convertedData = data.map((specimen: any) => ({
        id: specimen.id,
        part_code: specimen.partCode,
        part_type: specimen.partType,
        specimen_part_id: specimen.specimenPartId,
        collection_method: specimen.collectionMethod,
        collection_datetime: specimen.collectionDatetime,
        site: specimen.site,
        condition: specimen.condition,
        submitted_by: specimen.submittedBy,
        cpt_code: specimen.cptCode
      }));
      console.log('Converted data:', convertedData);
      return convertedData;
    } catch (error) {
      console.error('Error fetching order specimen parts:', error);
      return [];
    }
  }, []);

  const fetchOrderAttachments = async (orderId: string) => {
    try {
      if (!orderId || orderId.trim() === '') {
        console.warn('Order ID is empty or null, skipping attachments fetch');
        return [];
      }
      const data = await apiService.getOrderAttachments(orderId.toString());
      if (data.success && Array.isArray(data.attachments)) {
        // Convert backend attachment format to frontend format
        return data.attachments.map((attachment: any) => ({
          id: attachment.id,
          fileName: attachment.fileName,
          fileType: attachment.fileType,
          fileSize: attachment.fileSize,
          storagePath: attachment.storagePath,
          includeInReport: attachment.includeInReport,
          uploadedBy: attachment.uploadedBy,
          createdAt: attachment.createdAt,
          signedUrl: attachment.signedUrl, // Backend provides signed URL
          // Create a mock File object for compatibility with existing UI
          file: new File([], attachment.fileName, { type: attachment.fileType }),
          previewUrl: attachment.signedUrl // Use signed URL as preview
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching order attachments:', error);
      return [];
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSectionFieldChange = (field: string, value: any) => {
    setSectionFieldValues(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to determine if a field should be disabled
  const isFieldDisabled = (isBasicTabField = false) => {
    // Always disabled if finalized
    if (isFinalized) return true;
    
    // Always disabled if selected version is not the latest
    if (selectedVersion && selectedVersion.id !== orderVersions[0]?.id) return true;
    
    // Addendum mode: disable all fields except addendum tab
    if (isAddendumMode) return true;
    
    // Amendment mode: disable only basic tab fields, allow editing of other tabs
    if (isAmendMode && isBasicTabField) return true;
    
    // Otherwise, field is enabled
    return false;
  };

  const handleDialogSave = async () => {
    // If SIGNOUT section exists, user is on SIGNOUT tab, and we're updating an order, show confirmation
    if (hasSignOutSection && isOnSignOutTab && editingOrder && !isFinalized && !isAddendumMode && !isAmendMode) {
      setPendingSignOutAction(() => async () => {
        await performSignOut();
      });
      setShowSignOutConfirmation(true);
      return;
    }
    
    // Regular save
    await performSave();
  };

  const performSave = async () => {
    // Handle amendment creation separately
    if (isAmendMode && editingOrder) {
      try {
        // Increment major version for amendment (e.g., 1.5 → 2.0, 1 → 2)
        const currentVersion = editingOrder.version || '1';
        let newMajorVersion: string;
        
        if (currentVersion.includes('.')) {
          // If version has decimal (e.g., "1.5"), increment major version and reset minor to 0
          const [major] = currentVersion.split('.');
          newMajorVersion = (parseInt(major) + 1).toString();
        } else {
          // If version is just major (e.g., "1"), increment it
          newMajorVersion = (parseInt(currentVersion) + 1).toString();
        }
        
        // Create a NEW order record with the same accession ID but new major version
        const amendedOrderData = {
          ...form,
          accessionId: editingOrder.accessionId, // Keep same accession ID
          version: newMajorVersion,
          amendedFromId: editingOrder.id, // Reference to original order (UUID)
          amendedAt: new Date().toISOString(),
          amendedBy: currentUser?.id?.toString(),
          status: 'Amended',
          collectionDatetime: convertFromDateTimeLocal(form.collectionDatetime),
          orderDate: form.orderDate ? convertFromDateTimeLocal(form.orderDate) : editingOrder.orderDate,
          sectionValues: JSON.stringify({ ...sectionFieldValues, procedures: procedureList }),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Create the new amended order version
        const result = await apiService.createOrder(amendedOrderData);
        
        setSnackbar({ open: true, message: 'Amendment saved successfully', severity: 'success' });
        
        if (result.success || result.orderId) {
          onClose();
          if (onOrderSaved) {
            onOrderSaved();
          }
        }
        return;
      } catch (error) {
        console.error('Error saving amendment:', error);
        setSnackbar({ open: true, message: 'Failed to save amendment. Please try again.', severity: 'error' });
        return;
      }
    }

    // Handle addendum creation separately
    if (isAddendumMode && editingOrder) {
      try {
        // Validate addendum fields
        if (!addendumFields.reason.trim()) {
          setSnackbar({ open: true, message: 'Please provide a reason for the addendum', severity: 'error' });
          return;
        }
        if (!addendumFields.part_affected.trim()) {
          setSnackbar({ open: true, message: 'Please specify the part affected', severity: 'error' });
          return;
        }
        if (!addendumFields.comment.trim()) {
          setSnackbar({ open: true, message: 'Please provide a comment for the addendum', severity: 'error' });
          return;
        }

        // Create addendum record
        const addendumData = {
          reason: addendumFields.reason,
          partAffected: addendumFields.part_affected,
          comment: addendumFields.comment,
          createdBy: currentUser?.id ? parseInt(currentUser.id.toString()) : 0
        };

        const addendumResponse = await apiService.createAddendum(addendumData);
        
        // Increment minor version for addendum (e.g., 1 → 1.1, 1.5 → 1.6)
        const currentVersion = editingOrder.version || '1';
        let newVersion: string;
        
        if (currentVersion.includes('.')) {
          // If version has decimal (e.g., "1.5"), increment minor version
          const [major, minor] = currentVersion.split('.');
          const newMinor = (parseInt(minor) + 1).toString();
          newVersion = `${major}.${newMinor}`;
        } else {
          // If version is just major (e.g., "1"), make it "1.1"
          newVersion = `${currentVersion}.1`;
        }
        
        // Create a NEW order record with the same accession ID but new version
        const newOrderData = {
          ...editingOrder,
          id: undefined, // Remove ID so a new one is generated
          version: newVersion,
          addendaId: addendumResponse.id, // Link the addendum to the new order
          collectionDatetime: editingOrder.collectionDatetime, // Keep original datetime
          orderDate: editingOrder.orderDate, // Keep original order date
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Create the new order version
        await apiService.createOrder(newOrderData);
        
        // If no error was thrown, the request was successful (201 status)
        setSnackbar({ open: true, message: 'Addendum saved successfully', severity: 'success' });
        handleDialogClose();
        if (onOrderSaved) {
          onOrderSaved();
        }
        return;
      } catch (error) {
        console.error('Error saving addendum:', error);
        setSnackbar({ open: true, message: 'Failed to save addendum. Please try again.', severity: 'error' });
        return;
      }
    }

    const sectionValues = { ...sectionFieldValues, procedures: procedureList };
    // Remove sources from sectionValues if present
    if ('sources' in sectionValues) delete sectionValues.sources;
    
    // Generate accession ID if creating a new order
    let accessionId = form.accessionId;
    if (!editingOrder && form.accessionLocationId) {
      try {
        // Find the selected patient to get the human-readable patientId
        const selectedPatient = patients.find(p => String(p.id) === String(form.patientId));
        const humanReadablePatientId = selectedPatient?.patientId || form.patientId;
        
        const generatedAccessionId = await AccessionService.generateAccessionNumber({
          locationId: form.accessionLocationId,
          patientId: humanReadablePatientId,
        });
        accessionId = String(generatedAccessionId);
      } catch (error) {
        console.error('Error generating accession ID:', error);
        // Fallback to a simple timestamp-based ID
        accessionId = Date.now().toString();
      }
    } else if (!accessionId) {
      // Ensure accessionId is always a string, even if it's undefined
      accessionId = Date.now().toString();
    } else {
      // Ensure accessionId is always a string
      accessionId = String(accessionId);
    }
    
    const orderPayload = {
      ...form,
      accessionId,
      collectionDatetime: convertFromDateTimeLocal(form.collectionDatetime),
      orderDate: form.orderDate || new Date().toISOString(),
      createdBy: form.createdBy || currentUser?.id?.toString(),
      accessionedBy: form.accessionedBy || currentUser?.name,
      accessionedById: form.accessionedById || currentUser?.id?.toString(),
      specimen_parts: sources,
      sectionValues: JSON.stringify(sectionValues),
      autoCreatedOrderId: autoCreatedOrderId, // Include auto-created order ID
    };

    // Save the order first
    const result = await onSave(orderPayload);
    
    // Filter out attachments that have already been uploaded
    const unuploadedAttachments = attachments.filter(attachment => !attachment.uploaded);
    
    if (result.success && unuploadedAttachments.length > 0 && result.orderId) {
      // Upload remaining attachments directly to Firebase Storage
      try {
        const orderId = result.orderId;
        // Find the selected patient to get the human-readable patientId for attachments
        const selectedPatient = patients.find(p => String(p.id) === String(form.patientId));
        const patientId = selectedPatient?.patientId || form.patientId || '';
        const uploadedBy = currentUser?.name || 'Unknown User';
        
        for (const attachment of unuploadedAttachments) {
          // Upload file directly to Firebase Storage using Firebase SDK
          const uploadResponse = await apiService.uploadFileDirectly(
            attachment.file,
            {
              orderId,
              patientId,
              includeInReport: attachment.includeInReport.toString(),
              uploadedBy
            }
          );
          
          if (uploadResponse.success) {
            // Create attachment record in database
            const attachmentData = {
              orderId,
              patientId: form.patientId, // Use the UUID for database record
              fileName: uploadResponse.fileName,
              fileSize: attachment.file.size,
              fileType: attachment.file.type,
              storagePath: uploadResponse.storagePath,
              includeInReport: attachment.includeInReport,
              uploadedBy
            };
            
            console.log('Creating attachment record with data:', attachmentData);
            
            await apiService.createAttachmentRecord(attachmentData);
          }
        }
      } catch (error) {
        console.error('Error uploading attachments:', error);
        // Don't fail the entire save if attachments fail
      }
    }
    
    if (result.success) {
      onClose();
    }
  };

  const performSignOut = async () => {
    const sectionValues = { ...sectionFieldValues, procedures: procedureList };
    // Remove sources from sectionValues if present
    if ('sources' in sectionValues) delete sectionValues.sources;
    
    const orderPayload = {
      ...form,
      status: 'Completed / Finalized', // Set status to completed
      collectionDatetime: convertFromDateTimeLocal(form.collectionDatetime),
      orderDate: form.orderDate || new Date().toISOString(),
      createdBy: form.createdBy || currentUser?.id?.toString(),
      accessionedBy: form.accessionedBy || currentUser?.name,
      accessionedById: form.accessionedById || currentUser?.id?.toString(),
      specimen_parts: sources,
      sectionValues: JSON.stringify(sectionValues),
      autoCreatedOrderId: autoCreatedOrderId, // Include auto-created order ID
    };

    // Save the order with completed status
    const result = await onSave(orderPayload);
    
    // Filter out attachments that have already been uploaded
    const unuploadedAttachments = attachments.filter(attachment => !attachment.uploaded);
    
    if (result.success && unuploadedAttachments.length > 0 && result.orderId) {
      // Upload remaining attachments directly to Firebase Storage
      try {
        const orderId = result.orderId;
        // Find the selected patient to get the human-readable patientId for attachments
        const selectedPatient = patients.find(p => String(p.id) === String(form.patientId));
        const patientId = selectedPatient?.patientId || form.patientId || '';
        const uploadedBy = currentUser?.name || 'Unknown User';
        
        for (const attachment of unuploadedAttachments) {
          // Upload file directly to Firebase Storage using Firebase SDK
          const uploadResponse = await apiService.uploadFileDirectly(
            attachment.file,
            {
              orderId,
              patientId,
              includeInReport: attachment.includeInReport.toString(),
              uploadedBy
            }
          );
          
          if (uploadResponse.success) {
            // Create attachment record in database
            const attachmentData = {
              orderId,
              patientId: form.patientId, // Use the UUID for database record
              fileName: uploadResponse.fileName,
              fileSize: attachment.file.size,
              fileType: attachment.file.type,
              storagePath: uploadResponse.storagePath,
              includeInReport: attachment.includeInReport,
              uploadedBy
            };
            
            console.log('Creating attachment record with data:', attachmentData);
            
            await apiService.createAttachmentRecord(attachmentData);
          }
        }
      } catch (error) {
        console.error('Error uploading attachments:', error);
        // Don't fail the entire save if attachments fail
      }
    }
    
    if (result.success) {
      onClose();
    }
  };

  const handleDialogClose = () => {
    setInitialDataLoaded(false); // Reset for next time dialog opens
    setLoadedVersionsCache(new Map()); // Clear version cache
    onClose();
  };

  const handleVersionChange = useCallback(async (versionFromList: Order) => {
    setSelectedVersion(versionFromList);
    
    // Check if this version is already loaded in cache
    let fullVersion = loadedVersionsCache.get(versionFromList.id);
    
    // If not in cache, fetch the full version data
    if (!fullVersion) {
      setVersionLoading(true);
      try {
        const versionData = await apiService.getOrderById(versionFromList.id);
        fullVersion = versionData;
        
        // Cache the loaded version
        setLoadedVersionsCache(prev => new Map(prev).set(versionFromList.id, fullVersion!));
      } catch (error) {
        console.error('Error loading version:', error);
        // Fall back to the basic version data from the list
        fullVersion = versionFromList;
      } finally {
        setVersionLoading(false);
      }
    }

    // Update form with the full version data
    if (fullVersion) {
      setForm({ 
        ...fullVersion,
        collectionDatetime: convertToDateTimeLocal(fullVersion.collectionDatetime),
        status: fullVersion.status || 'In Progress'
      });
      
      // Parse sectionValues
      let parsedSectionValues: Record<string, any> = {};
      if ((fullVersion as any).sectionValues) {
        let sectionValues = (fullVersion as any).sectionValues;
        if (typeof sectionValues === 'string') {
          try {
            sectionValues = JSON.parse(sectionValues);
          } catch {
            sectionValues = {};
          }
        }
        parsedSectionValues = sectionValues;
      }
      setSectionFieldValues(parsedSectionValues);

      // Fetch specimen parts from API
      if (fullVersion.id && fullVersion.id.trim() !== '') {
        const parts = await fetchOrderSpecimenParts(fullVersion.id);
        setSources(parts);
      } else {
        setSources([]);
      }

      // Update procedures
      if (Array.isArray(parsedSectionValues.procedures) && parsedSectionValues.procedures.length > 0) {
        setProcedureList(parsedSectionValues.procedures);
      } else {
        setProcedureList([]);
      }
    }
  }, [loadedVersionsCache, setVersionLoading, fetchOrderSpecimenParts, setSources, setForm, setSectionFieldValues, setProcedureList]);

  // Initialize form when editingOrder changes
  useEffect(() => {
    if (editingOrder) {
      setForm({ 
        ...editingOrder,
        collectionDatetime: convertToDateTimeLocal(editingOrder.collectionDatetime)
      });
      
      // Parse sectionValues and set to sectionFieldValues
      let parsedSectionValues: Record<string, any> = {};
      if (editingOrder.sectionValues) {
        const sectionValues = editingOrder.sectionValues;
        if (typeof sectionValues === 'string') {
          try {
            parsedSectionValues = JSON.parse(sectionValues);
          } catch {
            parsedSectionValues = {};
          }
        } else if (typeof sectionValues === 'object') {
          parsedSectionValues = sectionValues;
        }
      }
      setSectionFieldValues(parsedSectionValues);

      // Fetch specimen parts from API
      if (editingOrder.id && editingOrder.id.trim() !== '') {
        fetchOrderSpecimenParts(editingOrder.id).then(parts => setSources(parts));
        // Fetch attachments from API
        fetchOrderAttachments(editingOrder.id).then(attachments => setAttachments(attachments));
      } else {
        setSources([]);
        setAttachments([]);
      }

      if (Array.isArray(parsedSectionValues.procedures) && parsedSectionValues.procedures.length > 0) {
        setProcedureList(parsedSectionValues.procedures);
      } else {
        setProcedureList([]);
      }

      // Clear cache when switching to a different order and cache the current editing order
      setLoadedVersionsCache(new Map([[editingOrder.id, editingOrder]]));
      
      // Fetch versions if this is an amended order
      if (editingOrder.version && editingOrder.version !== '1' && editingOrder.accessionId) {
        apiService.getOrderVersions(editingOrder.accessionId.toString())
          .then(data => {
            let versions = data.versions || [];
            
            // Sort versions to ensure latest is first (fallback in case backend doesn't sort)
            versions = versions.sort((a: any, b: any) => {
              const versionA = a.version || '1';
              const versionB = b.version || '1';
              
              // Compare versions (e.g., "2.1" vs "1.5")
              const parseVersion = (v: string) => {
                const parts = v.split('.').map(Number);
                return { major: parts[0] || 0, minor: parts[1] || 0 };
              };
              
              const vA = parseVersion(versionA);
              const vB = parseVersion(versionB);
              
              // Sort descending (latest first)
              if (vA.major !== vB.major) return vB.major - vA.major;
              return vB.minor - vA.minor;
            });
            
            setOrderVersions(versions);
            
            // Find the latest version (should be first in the sorted list)
            const latestVersion = versions[0];
            if (latestVersion) {
              setSelectedVersion(latestVersion);
            }
          })
          .catch(() => {
            setOrderVersions([]);
            setSelectedVersion(null);
          });
      } else {
        setOrderVersions([]);
        setSelectedVersion(null);
      }
      // Reset to first tab when switching orders, or Addendum tab if in addendum mode
      setSelectedTab(isAddendumMode ? 100 : 0);
      // Reset addendum fields
      setAddendumFields({
        reason: '',
        part_affected: '',
        comment: ''
      });
    } else {
      setForm({ 
        collectionDatetime: getLocalDateTimeString(),
        status: 'In Progress'
      });
      setSectionFieldValues({});
      setSources([]);
      setProcedureList([]);
      setOrderVersions([]);
      setSelectedVersion(null);
      setAttachments([]);
      // Reset to first tab for new orders, or Addendum tab if in addendum mode
      setSelectedTab(isAddendumMode ? 100 : 0);
      // Reset addendum fields
      setAddendumFields({
        reason: '',
        part_affected: '',
        comment: ''
      });
    }
  }, [editingOrder, isAddendumMode, handleVersionChange, fetchOrderSpecimenParts]);



  // Update form when users are loaded to ensure Autocomplete components work correctly
  // Only update if the current form values don't match the editing order
  useEffect(() => {
    if (editingOrder && users.length > 0) {
      const needsUpdate = form.orderingPhysician !== editingOrder.orderingPhysician || 
                         form.assignedPathologistId !== editingOrder.assignedPathologistId;
      
      if (needsUpdate) {
        setForm(prev => ({ 
          ...prev,
          orderingPhysician: editingOrder.orderingPhysician,
          assignedPathologistId: editingOrder.assignedPathologistId
        }));
      }
    }
  }, [editingOrder, users, form.orderingPhysician, form.assignedPathologistId]);

  // Fetch all initial data when dialog opens
  useEffect(() => {
    if (open && !initialDataLoaded) {
      const fetchInitialData = async () => {
        setDropdownLoading(true);
        setSpecimenClassesLoading(true);
        
        try {
          // Batch all initial API calls
          const [patientsData, usersData, locationsData, specimenClassesData] = await Promise.all([
            apiService.getPatients(1, 1000),
            apiService.getUsers(1, 1000),
            apiService.getAllLocations(),
            apiService.getSpecimenClasses(),
          ]);
          
          // Update all state at once to minimize re-renders
          setPatients(patientsData.patients || []);
          setUsers(Array.isArray(usersData.users) ? usersData.users : Array.isArray(usersData) ? usersData : []);
          setLocations(locationsData.locations || locationsData);
          setSpecimenClasses(Array.isArray(specimenClassesData) ? specimenClassesData : []);
          setInitialDataLoaded(true);
        } catch (error) {
          console.error('Error fetching initial data:', error);
          setPatients([]);
          setUsers([]);
          setLocations([]);
          setSpecimenClasses([]);
        } finally {
          setDropdownLoading(false);
          setSpecimenClassesLoading(false);
        }
      };
      
      fetchInitialData();
    }
  }, [open, initialDataLoaded]);

  // Fetch template sections when specimen class changes
  useEffect(() => {
    if (open && form.specimenClass) {
      const selectedClass = specimenClasses.find(sc => sc.label === form.specimenClass);
      if (selectedClass && selectedClass.id) {
        apiService.getSpecimenClassById(selectedClass.id)
          .then(data => {
            if (Array.isArray(data.sections) && data.sections.length > 0 && typeof data.sections[0] === 'object') {
              // Sort sections by order_num_on_template
              const sortedSections = [...data.sections].sort((a, b) => 
                (a.order_num_on_template ?? 0) - (b.order_num_on_template ?? 0)
              );
              setTemplateSections(sortedSections);
            } else if (Array.isArray(data.sections) && data.sections.length > 0) {
              Promise.all(data.sections.map((sid: any) => apiService.getSpecimenClassSectionById(sid)))
                .then(sections => {
                  // Sort sections by order_num_on_template
                  const sortedSections = [...sections].sort((a, b) => 
                    (a.order_num_on_template ?? 0) - (b.order_num_on_template ?? 0)
                  );
                  setTemplateSections(sortedSections);
                })
                .catch(() => setTemplateSections([]));
            } else {
              setTemplateSections([]);
            }
          })
          .catch((error) => {
            console.error('Error fetching specimen class sections:', error);
            setTemplateSections([]);
          });
      } else {
        setTemplateSections([]);
      }
    } else {
      setTemplateSections([]);
    }
  }, [open, form.specimenClass, specimenClasses]);

  // Extract addenda when order versions change
  useEffect(() => {
    // Extract addenda directly from the versions response (GraphQL already includes addendum data)
    const extractedAddenda = (orderVersions || [])
      .map((v: any) => v.addendum)
      .filter(Boolean); // Filter out null/undefined addenda
    setAddenda(extractedAddenda);
  }, [orderVersions]);

  // Focus on addendum form when in addendum mode
  useEffect(() => {
    if (isAddendumMode && open) {
      setTimeout(() => {
        reasonRef.current?.focus();
      }, 100);
    }
  }, [isAddendumMode, open]);

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const data = await apiService.getUserMe();
        setCurrentUser(data);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      attachments.forEach(attachment => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    };
  }, [attachments]);

  // Clear auto-created order ID when dialog closes or when editing a different order
  useEffect(() => {
    if (!open || editingOrder?.id) {
      setAutoCreatedOrderId(null);
    }
  }, [open, editingOrder?.id]);

  // File upload management functions
  
  const handleImmediateFileUpload = async (files: File[]) => {
    try {
      let orderId = editingOrder?.id || autoCreatedOrderId;
      
      // If we don't have an order ID (creating new order), create the order first
      if (!orderId) {
        console.log("No order ID found for file upload, creating order first...");
        
        // Validate required fields for order creation
        if (!form.patientId) {
          setSnackbar({ open: true, message: 'Please select a patient before uploading files.', severity: 'error' });
          return;
        }
        if (!form.specimenClass) {
          setSnackbar({ open: true, message: 'Please select a specimen class before uploading files.', severity: 'error' });
          return;
        }
        if (!form.collectionDatetime) {
          setSnackbar({ open: true, message: 'Please set a collection date/time before uploading files.', severity: 'error' });
          return;
        }
        
        try {
          // Create order with current form data
          const orderData = {
            patientId: form.patientId,
            collectionDatetime: convertFromDateTimeLocal(form.collectionDatetime),
            specimenClass: form.specimenClass,
            orderingPhysician: form.orderingPhysician || '',
            clinicalHistory: form.clinicalHistory || '',
            accessionLocationId: form.accessionLocationId,
            accessionedById: form.accessionedById,
            grossingTechId: form.grossingTechId,
            assignedPathologistId: form.assignedPathologistId,
            sectionValues: form.sectionValues,
            accessionId: form.accessionId,
            orderDate: form.orderDate ? convertFromDateTimeLocal(form.orderDate) : new Date().toISOString(),
            createdBy: form.createdBy,
            accessionedBy: form.accessionedBy,
            status: form.status || "In Progress",
            version: "1"
          };
          
          console.log("Creating order for file upload with data:", orderData);
          const createdOrder = await apiService.createOrder(orderData);
          orderId = createdOrder.id;
          
          // Store the auto-created order ID for subsequent operations
          setAutoCreatedOrderId(orderId);
          
          console.log("Order created successfully for file upload with ID:", orderId);
        } catch (orderError) {
          console.error('Error creating order for file upload:', orderError);
          alert('Failed to create order for file upload. Please check the required fields and try again.');
          return;
        }
      }
      
      // Now upload the files to the order
      const selectedPatient = patients.find(p => String(p.id) === String(form.patientId));
      const patientId = selectedPatient?.patientId || form.patientId || '';
      const uploadedBy = currentUser?.name || 'Unknown User';
      
      const successfulUploads: Attachment[] = [];
      
      for (const file of files) {
        try {
          console.log(`Uploading file: ${file.name}`);
          
          // Upload file directly to Firebase Storage
          const uploadResponse = await apiService.uploadFileDirectly(
            file,
            {
              orderId,
              patientId,
              includeInReport: isImageFile(file).toString(), // Default to true for images, false for PDFs
              uploadedBy
            }
          );
          
          if (uploadResponse.success) {
            // Create attachment record in database
            const attachmentData = {
              orderId,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              uploadedBy,
              includeInReport: isImageFile(file),
              storagePath: uploadResponse.storagePath,
              downloadUrl: uploadResponse.downloadURL
            };
            
            const attachmentRecord = await apiService.createAttachmentRecord(attachmentData);
            
            // Add to successful uploads with database ID
            successfulUploads.push({
              id: attachmentRecord.id,
              file,
              includeInReport: isImageFile(file),
              previewUrl: isImageFile(file) ? createPreviewUrl(file) : undefined,
              uploaded: true,
              downloadUrl: uploadResponse.downloadURL,
              filePath: uploadResponse.storagePath
            });
            
            console.log(`Successfully uploaded: ${file.name}`);
          } else {
            console.error(`Failed to upload file: ${file.name}`);
            alert(`Failed to upload file: ${file.name}`);
          }
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          alert(`Error uploading file: ${file.name}`);
        }
      }
      
      // Add successful uploads to attachments state
      if (successfulUploads.length > 0) {
        setAttachments(prev => [...prev, ...successfulUploads]);
        console.log(`Successfully uploaded ${successfulUploads.length} files`);
      }
      
    } catch (error) {
      console.error('Error in immediate file upload:', error);
      alert('Failed to upload files. Please try again.');
    }
  };

  // Specimen parts management functions

  const handleAddSpecimen = () => {
    // Generate next available part code
    const existingCodes = sources.map(specimen => specimen.part_code).filter(code => code && code.length > 0);
    let nextCode = 'A';
    
    if (existingCodes.length > 0) {
      // Find the highest letter used
      const letters = existingCodes.map(code => code.charAt(0).toUpperCase()).filter(letter => /^[A-Z]$/.test(letter));
      if (letters.length > 0) {
        const highestLetter = letters.reduce((max, letter) => 
          letter.charCodeAt(0) > max.charCodeAt(0) ? letter : max, 'A');
        const nextCharCode = highestLetter.charCodeAt(0) + 1;
        if (nextCharCode <= 90) { // 'Z' is 90
          nextCode = String.fromCharCode(nextCharCode);
        } else {
          // If we've used all letters A-Z, start over with A
          nextCode = 'A';
        }
      }
    }
    
    setEditingSpecimenIdx(null);
    setSpecimenForm({
      part_code: nextCode,
      part_type: '',
      collection_method: '',
      collection_datetime: getLocalDateTimeString(),
      collectionDatetime: getLocalDateTimeString(),
      site: '',
      condition: '',
      submitted_by: '',
      cpt_code: '',
    });
    setSpecimenDialogOpen(true);
  };

  const handleEditSpecimen = (index: number) => {
    setEditingSpecimenIdx(index);
    setSpecimenForm(sources[index]);
    setSpecimenDialogOpen(true);
  };

  const handleDeleteSpecimen = async (index: number) => {
    const specimen = sources[index];
    if (specimen.id) {
      try {
        await apiService.deleteOrderSpecimenPart(String(specimen.id));
        setSources(prev => prev.filter((_, i) => i !== index));
      } catch (error) {
        console.error('Error deleting specimen part:', error);
      }
    } else {
      setSources(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSpecimenSave = async (specimen: any) => {
    try {
      const convertedSpecimen = convertFromSpecimenDialogFormat(specimen);
      if (editingSpecimenIdx !== null) {
        // Update existing specimen
        if (convertedSpecimen.id) {
          await apiService.updateOrderSpecimenPart(String(convertedSpecimen.id), convertedSpecimen);
        }
        // Refresh the specimen list from server to ensure we have the latest data
        if (editingOrder?.id) {
          const updatedParts = await fetchOrderSpecimenParts(editingOrder.id);
          setSources(updatedParts);
        }
      } else {
        // Add new specimen
        let orderId = editingOrder?.id || autoCreatedOrderId;
        
        // If we don't have an order ID (creating new order), create the order first
        if (!orderId) {
          console.log("No order ID found, creating order first...");
          
          // Validate required fields for order creation
          if (!form.patientId) {
            setSnackbar({ open: true, message: 'Please select a patient before adding specimen parts.', severity: 'error' });
            return;
          }
          if (!form.specimenClass) {
            setSnackbar({ open: true, message: 'Please select a specimen class before adding specimen parts.', severity: 'error' });
            return;
          }
          if (!form.collectionDatetime) {
            setSnackbar({ open: true, message: 'Please set a collection date/time before adding specimen parts.', severity: 'error' });
            return;
          }
          
          try {
            // Generate accession ID for the new order
            let accessionId = form.accessionId;
            if (form.accessionLocationId) {
              try {
                // Find the selected patient to get the human-readable patientId
                const selectedPatient = patients.find(p => String(p.id) === String(form.patientId));
                const humanReadablePatientId = selectedPatient?.patientId || form.patientId;
                
                const generatedAccessionId = await AccessionService.generateAccessionNumber({
                  locationId: form.accessionLocationId,
                  patientId: humanReadablePatientId,
                });
                accessionId = String(generatedAccessionId);
                console.log("Generated accession ID for auto-created order:", accessionId);
              } catch (error) {
                console.error('Error generating accession ID for auto-created order:', error);
                // Fallback to a simple timestamp-based ID
                accessionId = Date.now().toString();
              }
            } else if (!accessionId) {
              // Ensure accessionId is always a string, even if it's undefined
              accessionId = Date.now().toString();
            } else {
              // Ensure accessionId is always a string
              accessionId = String(accessionId);
            }

            // Create order with current form data
            const orderData = {
              patientId: form.patientId,
              collectionDatetime: convertFromDateTimeLocal(form.collectionDatetime),
              specimenClass: form.specimenClass,
              orderingPhysician: form.orderingPhysician || '',
              clinicalHistory: form.clinicalHistory || '',
              accessionLocationId: form.accessionLocationId,
              accessionedById: form.accessionedById,
              grossingTechId: form.grossingTechId,
              assignedPathologistId: form.assignedPathologistId,
              sectionValues: form.sectionValues,
              accessionId: accessionId,
              orderDate: form.orderDate ? convertFromDateTimeLocal(form.orderDate) : new Date().toISOString(),
              createdBy: form.createdBy,
              accessionedBy: form.accessionedBy,
              status: form.status || "In Progress",
              version: "1"
            };
            
            console.log("Creating order with data:", orderData);
            const createdOrder = await apiService.createOrder(orderData);
            orderId = createdOrder.id;
            
            // Store the auto-created order ID for subsequent specimen parts
            setAutoCreatedOrderId(orderId);
            
            console.log("Order created successfully with ID:", orderId);
          } catch (orderError) {
            console.error('Error creating order:', orderError);
            setSnackbar({ open: true, message: 'Failed to create order. Please check the required fields and try again.', severity: 'error' });
            return;
          }
        }
        
        console.log("Original specimen data:", specimen);
        console.log("Converted specimen data:", convertedSpecimen);
        console.log("Final payload being sent:", {
          ...convertedSpecimen,
          orderId: orderId
        });
        console.log("collectionDatetime value:", convertedSpecimen.collectionDatetime);
        console.log("collectionDatetime type:", typeof convertedSpecimen.collectionDatetime);
        
        const newSpecimen = await apiService.createOrderSpecimenPart({
          ...convertedSpecimen,
          orderId: orderId
        });
        
        // Refresh the specimen list from server to ensure we have the latest data
        if (orderId) {
          try {
            const updatedParts = await fetchOrderSpecimenParts(String(orderId));
            setSources(updatedParts);
          } catch (fetchError) {
            console.error('Error fetching updated specimen parts:', fetchError);
            // Fallback to adding the new specimen locally if server fetch fails
            setSources(prev => [...prev, newSpecimen]);
          }
        } else {
          // Fallback if orderId is not available
          setSources(prev => [...prev, newSpecimen]);
        }
      }
      setSpecimenDialogOpen(false);
    } catch (error) {
      console.error('Error saving specimen part:', error);
      setSnackbar({ open: true, message: 'Failed to save specimen part. Please try again.', severity: 'error' });
    }
  };



  // Helper function to convert between SourceType interfaces
  const convertToSpecimenDialogFormat = (specimen: SourceType) => ({
    id: specimen.id,
    part_code: specimen.part_code,
    part_type: specimen.part_type,
    specimen_part_id: specimen.specimen_part_id,
    collection_method: specimen.collection_method,
    collection_datetime: specimen.collection_datetime,
    site: specimen.site,
    condition: specimen.condition,
    submitted_by: specimen.submitted_by,
    cpt_code: specimen.cpt_code,
    collectionDatetime: specimen.collection_datetime || specimen.collectionDatetime || getLocalDateTimeString()
  });

  const convertFromSpecimenDialogFormat = (specimen: any) => ({
    id: specimen.id,
    orderId: specimen.order_id,
    partCode: specimen.part_code,
    partType: specimen.part_type,
    specimenPartId: specimen.specimen_part_id,
    collectionMethod: specimen.collection_method,
    collectionDatetime: specimen.collectionDatetime,
    site: specimen.site,
    condition: specimen.condition,
    submittedBy: specimen.submitted_by,
    cptCode: specimen.cpt_code
  });

  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth={false} 
      fullWidth
      keepMounted={false}
      PaperProps={{ 
        sx: { 
          width: '70%', 
          height: '85%', 
          maxWidth: 'none', 
          maxHeight: 'none', 
          minWidth: 0, 
          minHeight: 0, 
          display: 'flex', 
          flexDirection: 'column' 
        } 
      }}
    >
      <DialogTitle>
        {isAddendumMode ? 'Edit Order - Adding an addendum' : 
         isAmendMode ? 'Edit Order - Amending' : 
         editingOrder ? 'Edit Order' : 
         autoCreatedOrderId ? 'Add Order (Auto-Created)' : 'Add Order'}
        {/* Show patient name and specimen class name if available */}
        {editingOrder && (
          <span style={{ fontWeight: 400, fontSize: 16, marginLeft: 8, color: 'text.secondary' }}>{editOrderLabel}</span>
        )}

        {/* Status dropdown under the title */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <TextField
             select
             size="small"
             label="Status"
             name="status"
             value={form.status || 'In Progress'}
             onChange={handleFormChange}
             required
                         sx={{ minWidth: 200 }}
            disabled={isFieldDisabled(true)}
           >
            {orderStatusOptions
              .filter(status => {
                if (form.status === "In Progress") {
                  return !["Amended", "Addendum"].includes(status);
                }
                if (form.status === "Amended") {
                  return ["Amended", "Completed / Finalized"].includes(status);
                }
                if (form.status === "Addendum") {
                  return ["Addendum", "Completed / Finalized"].includes(status);
                }
                return true;
              })
              .map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
          </TextField>
        </Box>
      </DialogTitle>

      {orderVersions.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={selectedVersion?.id || orderVersions[0]?.id}
            onChange={(_, newValue) => {
              const version = orderVersions.find(v => v.id === newValue);
              if (version) handleVersionChange(version);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {(orderVersions || []).map((version) => (
              <Tab
                key={version.id}
                label={`v${version.version || '1'}`}
                value={version.id}
              />
            ))}
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ flex: 1, overflowY: 'auto' }}>
        {(dropdownLoading || specimenClassesLoading || versionLoading) && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
          {versionLoading && <Typography variant="caption" sx={{ ml: 1, alignSelf: 'center' }}>Loading version...</Typography>}
        </Box>}
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 'auto',
                padding: '6px 12px',
                fontSize: '0.875rem'
              }
            }}
          >
            {(specialTabs || []).map(tab => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
            {(templateSections || [])
              .filter(section => !(hasSourceSection && section.name && section.name.toLowerCase() === 'source'))
              .map(section => {
                // Shorten long section names for better tab display
                const shortName = section.name && section.name.length > 12 
                  ? section.name.substring(0, 12) + '...' 
                  : section.name;
                return <Tab key={section.id} label={shortName} value={sectionTabMap[section.id]} />;
              })}
          </Tabs>
        </Box>

        {/* Basic Information Tab */}
        {selectedTab === 0 && (
          <>
            {/* Patient Information */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Patient Information</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, maxWidth: '400px' }}>
                  <Autocomplete
                    options={patients}
                    getOptionLabel={option => `${option.name} (${option.patientId})`}
                    value={patients.find(p => String(p.id) === String(form.patientId)) || null}
                    onChange={(_, value) => setForm(prev => ({ ...prev, patientId: value ? String(value.id) : undefined }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Patient"
                        required
                        disabled={isFieldDisabled(true)}
                      />
                    )}
                    disabled={isFieldDisabled(true)}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleOpenPatientDialog}
                  disabled={isFieldDisabled(true)}
                  startIcon={<AddIcon />}
                  sx={{
                    minWidth: '140px',
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    color: 'white',
                    fontWeight: 600,
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                      boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                    },
                    '&:disabled': {
                      background: '#ccc',
                      color: '#999'
                    }
                  }}
                >
                  Add Patient
                </Button>
              </Box>
            </Paper>

            {/* Collection Details */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Collection Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Collection Date/Time"
                    type="datetime-local"
                    name="collectionDatetime"
                    value={form.collectionDatetime || ''}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    disabled={isFieldDisabled(true)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={specimenClasses}
                    getOptionLabel={option => option.label || ''}
                    value={specimenClasses.find(sc => sc.label === form.specimenClass) || null}
                    onChange={(_, value) => setForm(prev => ({ ...prev, specimenClass: value?.label || '' }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Specimen Class"
                        required
                        disabled={isFieldDisabled(true)}
                      />
                    )}
                    disabled={isFieldDisabled(true)}
                  />
                </Grid>
                                 <Grid item xs={12} sm={6}>
                   <Autocomplete
                     options={labSiteLocations}
                     getOptionLabel={option => option.labName}
                     value={labSiteLocations.find(l => String(l.location_id) === String(form.accessionLocationId)) || null}
                     onChange={(_, value) => setForm(prev => ({ ...prev, accessionLocationId: value ? String(value.location_id) : '' }))}
                     renderInput={params => (
                       <TextField
                         {...params}
                         label="Accession Location"
                         required
                         disabled={isFieldDisabled(true)}
                       />
                     )}
                     disabled={isFieldDisabled(true)}
                   />
                 </Grid>
              </Grid>
            </Paper>

            {/* Clinical & Order Details */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Clinical & Order Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={option => option.name}
                    value={users.find(u => u.id.toString() === form.orderingPhysician?.toString()) || null}
                    onChange={(_, value) => setForm(prev => ({ ...prev, orderingPhysician: value?.id?.toString() || undefined }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Ordering Physician"
                        required
                        disabled={isFieldDisabled(true)}
                      />
                    )}
                    disabled={isFieldDisabled(true)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={option => option.name}
                    value={users.find(u => u.id.toString() === form.assignedPathologistId?.toString()) || null}
                    onChange={(_, value) => setForm(prev => ({ ...prev, assignedPathologistId: value?.id?.toString() || undefined }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Assigned Pathologist"
                        disabled={isFieldDisabled(true)}
                      />
                    )}
                    disabled={isFieldDisabled(true)}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <MacroTextField
                    label="Clinical History"
                    value={form.clinicalHistory || ''}
                    onChange={value => setForm(prev => ({ ...prev, clinicalHistory: value }))}
                    fullWidth
                    multiline
                    minRows={1}
                    sx={{ mt: 1 }}
                    disabled={isFieldDisabled(true)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {/* Template Sections */}
        {Array.isArray(templateSections) && (templateSections || [])
          .filter(section => !(hasSourceSection && section.name && section.name.toLowerCase() === 'source'))
          .map((section, idx) => {
            const tabIndex = sectionTabMap[section.id];
            if (selectedTab !== tabIndex) return null;
            return (
              <Paper key={section.id || idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1">{section.name}</Typography>
                {Array.isArray(section.fields)
                  ? section.fields.map((field: any, fidx: any) => {
                      const fieldName = (field.field_name || '').toLowerCase();
                      const isMacro =
                        fieldName === 'gross description' ||
                        fieldName === 'microscopic description' ||
                        fieldName === 'final diagnosis' ||
                        fieldName === 'final diagnosis (narrative)';
                      if (isMacro) {
                        return (
                          <MacroTextField
                            key={fidx}
                            label={field.field_name}
                            value={sectionFieldValues[field.field_name] || ''}
                            onChange={value =>
                              handleSectionFieldChange(field.field_name, value)
                            }
                            fullWidth
                            multiline
                            minRows={2}
                            sx={{ mt: 1 }}
                          />
                        );
                      }
                      // Check if this is a staff-related field that should be a dropdown
                      const isStaffField = fieldName.includes('pathologist') || 
                                         fieldName.includes('grossing') || 
                                         fieldName.includes('accessioned') || 
                                         fieldName.includes('cytotechnologist') || 
                                         fieldName.includes('transcriptionist') ||
                                         fieldName.includes('grossed by') ||
                                         fieldName.includes('accessioned by');
                      
                      // Check if this is a code field that should use CodeSearchDropdown
                      const dataType = String(field.data_type || '').toLowerCase();
                      const isCodeField = dataType.includes('icd_code') || 
                                        dataType.includes('cpt_code') || 
                                        dataType.includes('snomed_code') ||
                                        dataType.includes('icd_code') ||
                                        dataType.includes('cpt_code') ||
                                        dataType.includes('snomed_code');
                      
                      if (isCodeField) {
                        let codeType: 'icd' | 'cpt' | 'snomed' = 'icd';
                        if (dataType.includes('cpt_code') || dataType.includes('cpt')) {
                          codeType = 'cpt';
                        } else if (dataType.includes('snomed_code') || dataType.includes('snomed')) {
                          codeType = 'snomed';
                        }
                        
                        return (
                          <Box key={fidx} sx={{ mt: 1, mb: 1 }}>
                            <CodeSearchDropdown
                              codeType={codeType}
                              value={sectionFieldValues[field.field_name] || ''}
                              onChange={(value) =>
                                handleSectionFieldChange(field.field_name, value)
                              }
                              label={field.field_name}
                              disabled={isFieldDisabled(false)}
                              placeholder={`Search ${codeType.toUpperCase()} codes...`}
                            />
                          </Box>
                        );
                      }
                      
                      if (isStaffField) {
                        return (
                          <Autocomplete
                            key={fidx}
                            options={users}
                            getOptionLabel={option => option.name}
                            value={users.find(u => u.name === sectionFieldValues[field.field_name]) || null}
                            onChange={(_, value) =>
                              handleSectionFieldChange(field.field_name, value ? value.name : '')
                            }
                            renderInput={params => (
                              <TextField
                                {...params}
                                label={field.field_name}
                                fullWidth
                                margin="normal"
                                disabled={isFieldDisabled(false)}
                              />
                            )}
                            disabled={isFieldDisabled(false)}
                          />
                        );
                      }
                      
                      return (
                        <TextField
                          key={fidx}
                          label={field.field_name}
                          value={sectionFieldValues[field.field_name] || ''}
                          onChange={e =>
                            handleSectionFieldChange(field.field_name, e.target.value)
                          }
                          fullWidth
                          margin="normal"
                          multiline={field.type === 'textarea'}
                          minRows={field.type === 'textarea' ? 2 : 1}
                        />
                      );
                    })
                  : null}
              </Paper>
            );
          })}

        {/* Histology Section */}
        {(templateSections || []).map(section => {
          const tabValue = sectionTabMap[section.id];
          if (
            section.name &&
            section.name.toLowerCase() === 'histology' &&
            selectedTab === tabValue &&
            Array.isArray(sources) &&
            sources.filter(s => typeof s.id === 'number' && s.part_code && s.part_type).length > 0
          ) {
            return (
              <HistologySection
                key={section.id}
                order={editingOrder}
                parts={sources
                  .filter(s => typeof s.id === 'number' && s.part_code && s.part_type)
                  .map(s => ({
                    id: s.id as number,
                    code: s.part_code,
                    description: s.part_type,
                    site: s.site
                  }))}
                disabled={isFieldDisabled(false)}
              />
            );
          }
          return null;
        })}

        {/* Attachments Tab */}
        {selectedTab === 99 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Attachments</Typography>
              <Button
                variant="outlined"
                size="small"
                component="label"
                disabled={isFieldDisabled(false)}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Upload
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf"
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files) return;
                    
                    const validFiles = Array.from(files).filter(file => {
                      if (!isValidFileType(file)) {
                        alert(`File "${file.name}" is not a valid file type. Only images and PDFs are allowed.`);
                        return false;
                      }
                      return true;
                    });

                    if (validFiles.length === 0) return;

                    // Handle immediate upload with auto-order creation if needed
                    await handleImmediateFileUpload(validFiles);
                    
                    // Clear the input so the same file can be selected again
                    e.target.value = '';
                  }}
                />
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Upload images (JPEG, PNG, GIF, BMP, WebP) or PDF files only.
            </Typography>
            
            {attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No attachments uploaded.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {attachments.map((attachment, idx) => (
                  <Box key={attachment.id || idx} sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 1,
                    backgroundColor: '#fafafa'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {getFileIcon(attachment.file)}
                      <Typography variant="body2" sx={{ 
                        flex: 1, 
                        minWidth: 0, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        fontSize: '0.875rem'
                      }}>
                        {attachment.fileName || attachment.file.name}
                        {attachment.uploaded && (
                          <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                            ✓ Uploaded
                          </Typography>
                        )}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={attachment.includeInReport}
                            onChange={(e) => {
                              setAttachments(prev => 
                                prev.map((att, i) => 
                                  i === idx 
                                    ? { ...att, includeInReport: e.target.checked }
                                    : att
                                )
                              );
                            }}
                            disabled={isFieldDisabled(false)}
                          />
                        }
                        label={<Typography variant="caption">Include</Typography>}
                        sx={{ mr: 1 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async () => {
                          if (attachment.previewUrl) {
                            URL.revokeObjectURL(attachment.previewUrl);
                          }
                          
                          // If this is an existing attachment (has an id), delete it from the backend
                          if (attachment.id && editingOrder?.id) {
                            try {
                              // TODO: Add delete attachment API call here
                              // await apiService.deleteAttachment(attachment.id);
                              console.log('Delete attachment:', attachment.id);
                            } catch (error) {
                              console.error('Error deleting attachment:', error);
                            }
                          }
                          
                          setAttachments(prev => prev.filter((_, i) => i !== idx));
                        }}
                        disabled={isFieldDisabled(false)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    {/* Compact Image Preview */}
                    {isImageFile(attachment.file) && (attachment.previewUrl || attachment.signedUrl) && (
                      <Box sx={{ mt: 1 }}>
                        <CardMedia
                          component="img"
                          image={attachment.signedUrl || attachment.previewUrl}
                          alt={attachment.fileName || attachment.file.name}
                          sx={{ 
                            maxHeight: 120, 
                            maxWidth: '100%', 
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 0.5
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Specimen Parts Tab */}
        {selectedTab === 1 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Specimen Parts</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {sources.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<QrCodeIcon />}
                    onClick={() => setBarcodeGeneratorOpen(true)}
                    disabled={isFieldDisabled(false)}
                  >
                    Generate Barcodes
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSpecimen}
                  disabled={isFieldDisabled(false)}
                >
                  Add Specimen Part
                </Button>
              </Box>
            </Box>
            
            {sources.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No specimen parts added yet. Click "Add Specimen Part" to get started.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Part Code</TableCell>
                      <TableCell>Part Type</TableCell>
                      <TableCell>Collection Method</TableCell>
                      <TableCell>Collection Date/Time</TableCell>
                      <TableCell>Site</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Submitted By</TableCell>
                      <TableCell>CPT Code</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sources.map((specimen, index) => (
                      <TableRow key={index}>
                        <TableCell>{specimen.part_code}</TableCell>
                        <TableCell>{specimen.part_type}</TableCell>
                        <TableCell>{specimen.collection_method}</TableCell>
                        <TableCell>
                          {specimen.collection_datetime ? 
                            new Date(specimen.collection_datetime).toLocaleString() : 
                            '-'
                          }
                        </TableCell>
                        <TableCell>{specimen.site || '-'}</TableCell>
                        <TableCell>{specimen.condition || '-'}</TableCell>
                        <TableCell>{specimen.submitted_by || '-'}</TableCell>
                        <TableCell>{specimen.cpt_code || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditSpecimen(index)}
                                disabled={isFieldDisabled(false)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSpecimen(index)}
                                disabled={isFieldDisabled(false)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Addendum Tab */}
        {isAddendumMode && selectedTab === 100 && (
          <Box id="addendum-form" sx={{ mt: 4 }}>
            <Typography variant="h6">Add Addendum</Typography>
            <TextField
              label="Reason for Addendum"
              value={addendumFields.reason}
              onChange={e => setAddendumFields(f => ({ ...f, reason: e.target.value }))}
              fullWidth
              margin="normal"
              required
              inputRef={reasonRef}
            />
            <TextField
              label="Part Affected"
              value={addendumFields.part_affected}
              onChange={e => setAddendumFields(f => ({ ...f, part_affected: e.target.value }))}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Comment"
              value={addendumFields.comment}
              onChange={e => setAddendumFields(f => ({ ...f, comment: e.target.value }))}
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              required
            />
            {/* Show existing addenda below the form in compact format */}
            {addenda && addenda.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>Previous Addenda</Typography>
                {(addenda || []).map((add, idx) => (
                  <Paper key={add.id || idx} variant="outlined" sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {add.reason || 'No reason specified'}
                      </Typography>
                      {add.createdAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {new Date(add.createdAt).toLocaleDateString()} {new Date(add.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </Typography>
                      )}
                    </Box>
                    {add.partAffected && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 0.5 }}>
                        <strong>Part:</strong> {add.partAffected}
                      </Typography>
                    )}
                    {add.comment && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', fontStyle: 'italic' }}>
                        {add.comment}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {/* Amendment History Section */}
            {editingOrder?.accessionId && (
              <AmendmentHistory 
                accessionId={editingOrder.accessionId}
                mode="chain"
                showAddendumHistory={selectedTab === 100}
                addenda={addenda}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        {(!isFinalized || (isAddendumMode && selectedTab === 100)) && (
          <Button onClick={handleDialogSave} variant="contained">
            {(() => {
              let buttonText = "Save";
              
              if (isAddendumMode) {
                buttonText = "Save Addendum";
              } else if (editingOrder) {
                if (hasSignOutSection && isOnSignOutTab) {
                  buttonText = "Update and Sign Out";
                } else {
                  buttonText = "Update";
                }
              }
              
              // Debug logging in development
              if (process.env.NODE_ENV === 'development') {
                console.log('Button Text Debug:', {
                  editingOrder: !!editingOrder,
                  isAddendumMode,
                  hasSignOutSection,
                  isOnSignOutTab,
                  buttonText
                });
              }
              
              return buttonText;
            })()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    
    {/* Snackbar for notifications */}
    <Snackbar 
      open={snackbar.open} 
      autoHideDuration={6000} 
      onClose={() => setSnackbar({...snackbar, open: false})}
    >
      <Alert 
        onClose={() => setSnackbar({...snackbar, open: false})} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    
    {/* Sign Out Confirmation Dialog */}
    <Dialog open={showSignOutConfirmation} onClose={() => setShowSignOutConfirmation(false)}>
      <DialogTitle>Confirm Sign Out</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to sign out this order? This will set the status to "Completed / Finalized" and cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowSignOutConfirmation(false)}>Cancel</Button>
        <Button 
          onClick={async () => {
            setShowSignOutConfirmation(false);
            if (pendingSignOutAction) {
              await pendingSignOutAction();
            }
          }} 
          variant="contained" 
          color="primary"
        >
          Yes, Sign Out
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Patient Creation Dialog */}
    <Dialog open={openPatientDialog} onClose={handleClosePatientDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
        color: 'white',
        fontWeight: 600
      }}>
        Add New Patient
      </DialogTitle>
      <form onSubmit={handlePatientSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient ID"
                value={patientFormData.patientId}
                onChange={(e) => setPatientFormData({ ...patientFormData, patientId: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={patientFormData.dob}
                onChange={(e) => setPatientFormData({ ...patientFormData, dob: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={patientFormData.name}
                onChange={(e) => setPatientFormData({ ...patientFormData, name: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Sex"
                value={patientFormData.sex}
                onChange={(e) => setPatientFormData({ ...patientFormData, sex: e.target.value })}
                required
                sx={{ mb: 2 }}
              >
                {sexOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MRN"
                value={patientFormData.mrn}
                onChange={(e) => setPatientFormData({ ...patientFormData, mrn: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Location"
                value={patientFormData.locationId}
                onChange={(e) => setPatientFormData({ ...patientFormData, locationId: e.target.value })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>No location assigned</em>
                </MenuItem>
                                                 {patientLocations.map((location) => (
                  <MenuItem key={location.location_id} value={String(location.location_id)}>
                    {location.labName} ({location.labCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleClosePatientDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            Create Patient
          </Button>
        </DialogActions>
      </form>
    </Dialog>

    {/* Specimen Dialog */}
    <SpecimenDialog
      open={specimenDialogOpen}
      onClose={() => setSpecimenDialogOpen(false)}
      editingSpecimenIdx={editingSpecimenIdx}
      specimenForm={convertToSpecimenDialogFormat(specimenForm)}
      onSave={async (specimen: any) => await handleSpecimenSave(specimen)}
      disabled={isFieldDisabled(false)}
    />

    {/* Barcode Generator Dialog */}
    <BarcodeGenerator
      open={barcodeGeneratorOpen}
      onClose={() => setBarcodeGeneratorOpen(false)}
      specimens={sources}
      orderId={editingOrder?.id}
      accessionId={editingOrder?.accessionId}
    />
    </>
  );
};

export default OrderDialog; 